import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import cnames from 'classnames';
import { FormattedMessage, injectIntl } from 'react-intl';
import gql from 'graphql-tag';

import * as constants from '@redbubble/design-system/react/constants';
import Button from '@redbubble/design-system/react/Button';
import Box from '@redbubble/design-system/react/Box';

import FirstInView from '../FirstInView';
import Loading from '../Loading';
import ErrorWithRetry from '../ErrorWithRetry';
import { useSignUpModal } from '../../contexts/SignUpModal';
import { useWorkFavoriteActions } from '../../containers/apollo/withToggleListFavorite';

import useArtworkMasonry from './useArtworkMasonry';
import Artwork, { fragments as artworkFragments } from './Artwork';
import styles from './ArtworkMasonry.css';
import messages from './messages';

const ArtworkMasonry = ({
  works,
  loading,
  error,
  refetch,
  fetchMore,
  canFetchMore,
  fetchingMore,
  analyticsLabel,
  locale,
  isLoggedIn,
  onContentShowed,
  onLoadingShowed,
  intl,
  logImpression,
  onFavorite
}) => {
  const [openModal] = useSignUpModal();
  const hasWorks = Array.isArray(works) && works.length > 0;

  useEffect(() => {
    if (!loading && hasWorks && canFetchMore) {
      fetchMore();
    } else if (!loading && !hasWorks) {
      refetch();
    }
  }, []);

  const {
    ref,
    itemRefs,
    displayable,
    initialLayoutComplete,
    mounted
  } = useArtworkMasonry({ loading, works });

  const {
    addWorkToFavorites,
    removeWorkFromFavorites
  } = useWorkFavoriteActions();

  const attemptFeedRender = !loading && mounted;
  const renderError = attemptFeedRender && (!!error || !hasWorks);

  const classNames = cnames(styles.grid, styles.feedLayout, {
    [styles.hidden]: !initialLayoutComplete
  });

  return (
    <Fragment>
      {!attemptFeedRender && (
        <Fragment>
          <FirstInView onFirstView={onLoadingShowed} />
          <Loading />
        </Fragment>
      )}

      <div className={classNames} ref={ref}>
        {attemptFeedRender && (
          <Fragment>
            <FirstInView onFirstView={onContentShowed} />
            {works.map((work, index) => (
              <div
                key={work.id}
                className={cnames(styles.gridItem, {
                  [styles.hidden]: !displayable.has(work.id)
                })}
                ref={node => {
                  itemRefs[work.id] = node;
                }}
              >
                <Artwork
                  work={work}
                  analyticsLabel={analyticsLabel}
                  index={index}
                  locale={locale}
                  openSignUpModal={openModal}
                  isLoggedIn={isLoggedIn}
                  intl={intl}
                  loadImage={displayable.has(work.id)}
                  logImpression={logImpression}
                  addWorkToFavorites={addWorkToFavorites}
                  removeWorkFromFavorites={removeWorkFromFavorites}
                  onFavorite={onFavorite}
                />
              </div>
            ))}
          </Fragment>
        )}
      </div>

      {renderError && (
        <ErrorWithRetry
          message={intl.formatMessage(messages.ErrorMessage)}
          retry={refetch}
        />
      )}

      {attemptFeedRender && initialLayoutComplete && canFetchMore && (
        <Box display="flex" justifyContent="center">
          <Button
            loading={fetchingMore}
            strong
            intent={constants.PRIMARY}
            size={constants.LARGE}
            onClick={fetchMore}
          >
            <FormattedMessage defaultMessage="Show More" />
          </Button>
        </Box>
      )}
    </Fragment>
  );
};

ArtworkMasonry.propTypes = {
  onContentShowed: PropTypes.func,
  onLoadingShowed: PropTypes.func,
  fetchMore: PropTypes.func,
  refetch: PropTypes.func,
  logImpression: PropTypes.func,
  onFavorite: PropTypes.func
};

ArtworkMasonry.defaultProps = {
  onContentShowed: () => {},
  onLoadingShowed: () => {},
  fetchMore: () => {},
  refetch: () => {},
  logImpression: () => {},
  onFavorite: () => {}
};

const WORK_TYPE = 'Work';
const WORK_FRAGMENT_NAME = 'WorkFieldsForArtworkMasonry';
const WORK_FRAGMENT = gql`
  fragment ${WORK_FRAGMENT_NAME} on ${WORK_TYPE} {
    id
  }
`;

export const fragments = [
  {
    type: WORK_TYPE,
    name: WORK_FRAGMENT_NAME,
    fragment: WORK_FRAGMENT
  },
  ...artworkFragments
];

export default injectIntl(ArtworkMasonry);
